import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UserDetailModal from "./UserDetailModal";
import { getBudgetTextColor } from "./budgetColors";
import { useGetCopilotBudgetSummaryQuery, useGetEnterpriseCopilotBudgetSummaryQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useDebounce } from "utils/useDebounce";

const BUDGET_RISK_ACCENT = "#E2231A";
const USAGE_ORANGE = "#F28C00";
const USAGE_YELLOW = "#FFCD00";
const ROWS_PER_PAGE = 20;

const getBudgetColor = (pct) => {
  if (pct >= 80) return BUDGET_RISK_ACCENT;
  if (pct >= 50) return USAGE_ORANGE;
  return USAGE_YELLOW;
};

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};

const bodyCellSx = {
  fontSize: "0.85rem",
  color: "#1a1a1a",
  py: 1.2,
  borderBottom: "1px solid #f0f0f0",
};

const formatSpend = (v) =>
  "$" + (v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BudgetAtRiskModal = ({ open, onClose, orgId, costCenterId, platformId, thresholdPct, deloitteOverageRate, isEnterprise = false }) => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const skip = isEnterprise ? !open || !platformId : !open || !(orgId || costCenterId) || !platformId;

  const scopedQuery = useGetCopilotBudgetSummaryQuery(
    { orgId, costCenterId, platformId, page, perPage: ROWS_PER_PAGE, search: debouncedSearch },
    { skip: skip || isEnterprise }
  );
  const enterpriseQuery = useGetEnterpriseCopilotBudgetSummaryQuery(
    { platformId, page, perPage: ROWS_PER_PAGE, search: debouncedSearch },
    { skip: skip || !isEnterprise }
  );
  const { data, isLoading, isFetching } = isEnterprise ? enterpriseQuery : scopedQuery;

  const rows = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

  const handleClose = () => {
    setPage(1);
    setSearchQuery("");
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `3px solid ${BUDGET_RISK_ACCENT}`,
            py: 2,
            px: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
              Users Near Budget Limit
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>
              {totalCount} user{totalCount === 1 ? "" : "s"} &ge;{thresholdPct}% consumed
              {searchQuery && ` · matching "${searchQuery}"`}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "1.2rem", color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: "#f9f9f9",
                },
              }}
            />
            <IconButton onClick={handleClose} size="small" sx={{ color: "#555" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: 560, position: "relative" }}>
          {/* Budget consumption is a running total per user with no period boundary,
              so this list is always based on the latest usage — it's independent of
              the dashboard's date range picker and will not change if that range is adjusted. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 3,
              py: 1,
              backgroundColor: "#fff8e1",
              borderBottom: "1px solid #ffe082",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "#a17f00" }} />
            <Typography sx={{ fontSize: "0.78rem", color: "#7a6100" }}>
              Based on each user's latest all-time usage — not scoped to the date range selected above.
            </Typography>
          </Box>

          {(isLoading || isFetching) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                zIndex: 10,
                borderRadius: "12px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <CircularProgress sx={{ color: BUDGET_RISK_ACCENT }} size={40} />
                <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>Loading...</Typography>
              </Box>
            </Box>
          )}

          {!isLoading && !isFetching && rows.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>
                No users found{searchQuery ? " for this search" : ""}.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1, overflowY: "auto", opacity: (isLoading || isFetching) ? 0.5 : 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, width: 52 }}>#</TableCell>
                    <TableCell sx={headerCellSx}>Email</TableCell>
                    {isEnterprise && <TableCell sx={headerCellSx}>Org</TableCell>}
                    <TableCell sx={headerCellSx} align="right">Budget Used</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => {
                    const budgetAmount = row.budget?.budget_amount ?? 0;
                    const consumedAmount = row.budget?.consumed_amount ?? 0;
                    const pct = budgetAmount > 0 ? Math.min((consumedAmount / budgetAmount) * 100, 100) : 0;
                    const barColor = getBudgetColor(pct);
                    return (
                      <TableRow
                        key={row.email}
                        onClick={() => setSelectedUser(row)}
                        sx={{
                          "&:hover": { backgroundColor: "#fdf3f2", cursor: "pointer" },
                          "&:last-child td": { border: 0 },
                        }}
                      >
                        <TableCell sx={{ ...bodyCellSx, color: "#999", width: 52, verticalAlign: "top", pt: 1.5 }}>
                          {(page - 1) * ROWS_PER_PAGE + idx + 1}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, verticalAlign: "top" }}>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
                            {row.email}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: "#999" }}>
                            {row.full_name}
                          </Typography>
                        </TableCell>
                        {isEnterprise && (
                          <TableCell sx={{ ...bodyCellSx, verticalAlign: "top", pt: 1.5 }}>
                            {row.org_name && (
                              <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>{row.org_name}</Typography>
                            )}
                            {row.cost_center_name && (
                              <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>{row.cost_center_name}</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ ...bodyCellSx, verticalAlign: "top", pt: 1.5, minWidth: 120 }} align="right">
                          <Tooltip
                            title={`${formatSpend(consumedAmount)} of ${formatSpend(budgetAmount)} budget consumed`}
                            placement="left"
                            arrow
                          >
                            <Box>
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: getBudgetTextColor(barColor), mb: 0.3 }}>
                                {pct.toFixed(0)}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{
                                  height: 4,
                                  borderRadius: 4,
                                  backgroundColor: "#f0f0f0",
                                  "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 4 },
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 1.5,
              backgroundColor: "#fafafa",
              opacity: (isLoading || isFetching) ? 0.5 : 1,
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: "#777" }}>
              Showing {totalCount === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1}–
              {Math.min(page * ROWS_PER_PAGE, totalCount)} of {totalCount} users
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                disabled={page === 1 || isLoading || isFetching}
                onClick={() => setPage((p) => p - 1)}
                sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: "0.82rem", color: "#555", minWidth: 70, textAlign: "center" }}>
                Page {page} of {totalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={page === totalPages || isLoading || isFetching}
                onClick={() => setPage((p) => p + 1)}
                sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* No enterprise-scoped day-by-day detail endpoint exists, so fall back
          to the selected row's own org_id in enterprise mode. */}
      <UserDetailModal
        user={selectedUser}
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        orgId={isEnterprise ? selectedUser?.org_id : orgId}
        costCenterId={isEnterprise ? undefined : costCenterId}
        platformId={platformId}
        deloitteOverageRate={deloitteOverageRate}
      />
    </>
  );
};

export default BudgetAtRiskModal;
